'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Search, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { capture as capturePH } from '@/lib/posthog';

/**
 * Header — Aviado Research chrome.
 *
 * Spec: design_handoff_aviado_labs/design-references/shared.css `.labs-top`.
 *   - Sticky, 64–68px tall, parchment bg w/ backdrop-blur
 *   - 3-col grid: lockup (left) / nav (center) / search + CTA (right)
 *   - Active underline: 2px warm-navy, 1.5px border-bottom
 *   - Mobile: hamburger → slide-down sheet
 *
 * Lockup uses a header-sized derivative of the canonical Aviado Research
 * asset, so the nav does not download the original 3945px-wide PNG.
 */

type NavKey = 'home' | 'browse' | 'longevity' | 'goals' | 'biomarkers' | 'methodology';

interface NavLink {
  key: NavKey;
  label: string;
  href: string;
}

const NAV_LINKS: NavLink[] = [
  { key: 'home',         label: 'Latest',            href: '/' },
  { key: 'browse',       label: 'Browse',            href: '/browse' },
  { key: 'longevity',    label: 'Longevity Daily',   href: '/longevity-daily' },
  { key: 'goals',        label: 'Health goals',      href: '/pillars' },
  { key: 'biomarkers',   label: 'Biomarkers',        href: '/biomarkers' },
  { key: 'methodology',  label: 'Methodology',       href: '/methodology' },
];

function activeKeyFromPath(pathname: string): NavKey | null {
  if (pathname === '/' || pathname === '') return 'home';
  // Browse is the single content-discovery hub now: /browse, article pages, and
  // supplement entity pages all light up the Browse tab (Articles + Supplements
  // A–Z were removed from the header; both remain in the footer).
  if (
    pathname.startsWith('/browse') ||
    pathname.startsWith('/articles') ||
    pathname.startsWith('/supplements')
  )
    return 'browse';
  if (pathname.startsWith('/longevity-daily')) return 'longevity';
  if (pathname.startsWith('/pillars')) return 'goals';
  if (pathname.startsWith('/biomarkers')) return 'biomarkers';
  if (pathname.startsWith('/methodology')) return 'methodology';
  return null;
}

export function Header() {
  const pathname = usePathname() ?? '/';
  const router = useRouter();
  const active = activeKeyFromPath(pathname);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Global Cmd+K / Ctrl+K — routes to /browse with focus on search.
  // Skip when the user is typing into an input / textarea / contenteditable,
  // so typing ⌘K in a search field doesn't yank focus away mid-query.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k')) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      // On /browse the live input is the Cmd+K consumer; skip the nav.
      if (pathname.startsWith('/browse')) return;
      e.preventDefault();
      router.push('/browse?focus=search');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [router, pathname]);

  // Close mobile menu on route change (best-effort)
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <header
      className={cn(
        'sticky top-0 z-50 w-full',
        'border-b border-border',
        'bg-[rgba(247,244,240,0.82)] backdrop-blur-[14px] backdrop-saturate-150'
      )}
    >
      <div className="max-w-container mx-auto px-10">
        <div className="grid grid-cols-[auto_1fr_auto] gap-8 items-center py-[14px]">
          {/* Lockup — exits the research library back to aviado.com main site.
              The Labs /research home is still reachable via the "Latest" nav link. */}
          <Link
            href="https://aviado.com"
            rel="noopener"
            className="inline-flex items-center no-underline"
            aria-label="Aviado Research — main site"
          >
            <picture>
              <source srcSet="/research/aviado-research-header.webp" type="image/webp" />
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/research/aviado-research-header.png"
                alt="Aviado Research"
                width={173}
                height={50}
                className="h-11 w-auto"
              />
            </picture>
          </Link>

          {/* Desktop nav */}
          <nav
            aria-label="Research sections"
            className="hidden lg:flex justify-self-center items-center gap-6 font-ui text-[16px] font-semibold text-ink whitespace-nowrap"
          >
            {NAV_LINKS.map((link) => (
              <Link
                key={link.key}
                href={link.href}
                aria-current={active === link.key ? 'page' : undefined}
                onClick={() => capturePH('nav_link_clicked', { nav_key: link.key, label: link.label })}
                className={cn(
                  'py-1.5 border-b-[1.5px] border-transparent no-underline text-ink',
                  'hover:text-aviado-blue transition-colors duration-fast',
                  active === link.key && 'border-b-warm-navy hover:!text-warm-navy'
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right rail */}
          <div className="hidden lg:flex items-center gap-3">
            <SearchPill />
            <Link
              href="https://www.aviado.com/auth"
              rel="noopener"
              onClick={() => capturePH('get_aviado_clicked', { placement: 'header_desktop' })}
              className={cn(
                'inline-flex min-h-12 items-center gap-1.5 px-4 py-2 rounded-full',
                'bg-warm-navy text-white',
                'font-ui text-[13px] font-medium no-underline',
                'hover:bg-aviado-blue transition-colors duration-fast'
              )}
            >
              Get Aviado
            </Link>
          </div>

          {/* Mobile toggle */}
          <div className="lg:hidden justify-self-end flex items-center gap-2">
            <Link
              href="/browse"
              aria-label="Open search"
              className="inline-flex min-h-12 min-w-12 items-center justify-center rounded-md text-ink/70 hover:text-ink hover:bg-black/5 transition-colors duration-fast"
            >
              <Search className="w-5 h-5" />
            </Link>
            <button
              type="button"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((o) => !o)}
              className="inline-flex min-h-12 min-w-12 items-center justify-center rounded-md text-ink/70 hover:text-ink hover:bg-black/5 transition-colors duration-fast"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile sheet */}
      {mobileOpen && (
        <nav
          aria-label="Research sections (mobile)"
          className="lg:hidden border-t border-border bg-parchment animate-fade-in"
        >
          <ul className="max-w-container mx-auto px-6 py-4 space-y-1 list-none">
            {NAV_LINKS.map((link) => (
              <li key={link.key}>
                <Link
                  href={link.href}
                  className="block min-h-12 px-3 py-3 rounded-md font-ui text-[14px] text-ink hover:bg-black/5 transition-colors duration-fast"
                >
                  {link.label}
                </Link>
              </li>
            ))}
            <li className="pt-3 border-t border-border">
              <Link
                href="https://www.aviado.com/auth"
                rel="noopener"
                onClick={() => capturePH('get_aviado_clicked', { placement: 'header_mobile' })}
                className={cn(
                  'block min-h-12 text-center px-4 py-3 rounded-full',
                  'bg-warm-navy text-white font-ui text-[13px] font-medium',
                  'hover:bg-aviado-blue transition-colors duration-fast'
                )}
              >
                Get Aviado
              </Link>
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
}

// ---------- Subcomponents ----------

function SearchPill() {
  return (
    <Link
      href="/browse?focus=search"
      className={cn(
        'inline-flex min-h-12 items-center gap-2 px-4 py-2 rounded-full',
        'bg-[rgba(26,29,38,0.04)] border border-border',
        'text-slate no-underline',
        'hover:border-border-strong hover:bg-white transition-colors duration-fast',
        'font-mono text-[11.5px] tracking-[0.02em]',
        'min-w-[220px]'
      )}
      aria-label="Search the library"
    >
      <Search className="w-[14px] h-[14px]" strokeWidth={2} />
      <span>Search articles</span>
      <kbd
        className={cn(
          'ml-auto font-mono text-[10px] text-slate',
          'bg-white border border-border rounded-[4px] px-[5px] py-px'
        )}
      >
        ⌘K
      </kbd>
    </Link>
  );
}

export default Header;
